import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Circle, AlertCircle, User, CheckCircle, ArrowLeft } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import './SignInPage.css';

type AuthMode = 'signin' | 'signup' | 'forgot-password';

interface SignInPageProps {
  onSignIn: () => void;
}

export const SignInPage: React.FC<SignInPageProps> = ({ onSignIn }) => {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else {
        onSignIn();
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await signUp(email, password, fullName);
      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage('Account created! Please check your email to verify your account.');
        setMode('signin');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await resetPassword(email);
      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage('Password reset link sent! Check your email.');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setError('');
    setSuccessMessage('');
  };

  const switchMode = (newMode: AuthMode) => {
    resetForm();
    setMode(newMode);
  };

  const renderSignIn = () => (
    <>
      <div className="signin-header">
        <div className="app-logo">
          <div className="logo-icon">
            <Circle size={24} />
          </div>
          <h1>TowApp</h1>
        </div>
        <p className="signin-subtitle">Welcome back! Sign in to your account</p>
      </div>

      <form className="signin-form" onSubmit={handleSignIn}>
        <div className="input-group">
          <label htmlFor="email">Email</label>
          <div className="input-wrapper">
            <Mail className="input-icon" size={20} />
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="password">Password</label>
          <div className="input-wrapper">
            <Lock className="input-icon" size={20} />
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            <CheckCircle size={16} />
            <span>{successMessage}</span>
          </div>
        )}

        <div className="form-footer">
          <label className="remember-me">
            <input type="checkbox" />
            <span>Remember me</span>
          </label>
          <button 
            type="button" 
            className="forgot-password-btn"
            onClick={() => switchMode('forgot-password')}
          >
            Forgot password?
          </button>
        </div>

        <button type="submit" className="signin-button" disabled={isLoading}>
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <div className="signin-footer">
        <p>
          Don't have an account?{' '}
          <button 
            type="button" 
            className="signup-link-btn"
            onClick={() => switchMode('signup')}
          >
            Create account
          </button>
        </p>
      </div>
    </>
  );

  const renderSignUp = () => (
    <>
      <div className="signin-header">
        <button 
          type="button" 
          className="back-button"
          onClick={() => switchMode('signin')}
        >
          <ArrowLeft size={20} />
        </button>
        <div className="app-logo">
          <div className="logo-icon">
            <Circle size={24} />
          </div>
          <h1>TowApp</h1>
        </div>
        <p className="signin-subtitle">Create your account</p>
      </div>

      <form className="signin-form" onSubmit={handleSignUp}>
        <div className="input-group">
          <label htmlFor="fullName">Full Name</label>
          <div className="input-wrapper">
            <User className="input-icon" size={20} />
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              required
              autoComplete="name"
            />
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="email">Email</label>
          <div className="input-wrapper">
            <Mail className="input-icon" size={20} />
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="password">Password</label>
          <div className="input-wrapper">
            <Lock className="input-icon" size={20} />
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min 6 characters)"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <div className="input-wrapper">
            <Lock className="input-icon" size={20} />
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            <CheckCircle size={16} />
            <span>{successMessage}</span>
          </div>
        )}

        <button type="submit" className="signin-button" disabled={isLoading}>
          {isLoading ? 'Creating account...' : 'Create Account'}
        </button>
      </form>

      <div className="signin-footer">
        <p>
          Already have an account?{' '}
          <button 
            type="button" 
            className="signup-link-btn"
            onClick={() => switchMode('signin')}
          >
            Sign in
          </button>
        </p>
      </div>
    </>
  );

  const renderForgotPassword = () => (
    <>
      <div className="signin-header">
        <button 
          type="button" 
          className="back-button"
          onClick={() => switchMode('signin')}
        >
          <ArrowLeft size={20} />
        </button>
        <div className="app-logo">
          <div className="logo-icon">
            <Circle size={24} />
          </div>
          <h1>TowApp</h1>
        </div>
        <p className="signin-subtitle">Reset your password</p>
      </div>

      <form className="signin-form" onSubmit={handleForgotPassword}>
        <p className="reset-instructions">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        <div className="input-group">
          <label htmlFor="email">Email</label>
          <div className="input-wrapper">
            <Mail className="input-icon" size={20} />
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            <CheckCircle size={16} />
            <span>{successMessage}</span>
          </div>
        )}

        <button type="submit" className="signin-button" disabled={isLoading}>
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </button>
      </form>

      <div className="signin-footer">
        <p>
          Remember your password?{' '}
          <button 
            type="button" 
            className="signup-link-btn"
            onClick={() => switchMode('signin')}
          >
            Sign in
          </button>
        </p>
      </div>
    </>
  );

  return (
    <div className="signin-page">
      <div className="signin-container">
        {mode === 'signin' && renderSignIn()}
        {mode === 'signup' && renderSignUp()}
        {mode === 'forgot-password' && renderForgotPassword()}
      </div>
    </div>
  );
};
